import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Revenue is the total money a company brings in from sales. Earnings (also called net income or profit) is what's left after all costs, interest, and taxes. A company with $10B in revenue and $1B in net income has a 10% net margin. Revenue measures size and demand. Earnings measure whether the business actually makes money.";

const FAQS = [
  { q: 'Is revenue the same as sales?', a: 'In most companies, yes. "Revenue" is the more formal accounting term and "sales" is the everyday word, but they refer to the same line on the income statement.' },
  { q: 'Why is revenue at the top of the income statement?', a: 'Income statements work top to bottom: revenue is the starting point, costs and expenses are subtracted in order, and net income (earnings) is the final result. That\'s why revenue is often called the "top line" and earnings the "bottom line".' },
  { q: 'Can a company grow revenue but still lose money?', a: 'Yes — and many startups do. Amazon famously had years of revenue growth with minimal profits because it reinvested everything. The risk is when revenue growth slows and losses persist.' },
  { q: 'Which one drives stock prices more?', a: 'Both, but in different ways. In the short term, beating earnings expectations moves stocks the most. In the long term, sustained revenue growth combined with expanding margins is what compounds the most.' },
  { q: 'What\'s "operating income" then?', a: 'Operating income sits between revenue and net income. It\'s revenue minus COGS and operating expenses, but before interest and taxes. It\'s often the cleanest measure of underlying business profitability.' },
];

export default function RevenueVsEarningsPage() {
  return (
    <BlogArticleLayout
      slug="revenue-vs-earnings"
      title="Revenue vs Earnings: What's the Difference?"
      description="Revenue and earnings get confused all the time. Here's what each really means, why earnings can be misleading, and how to read them together."
      keywords="revenue vs earnings, revenue vs net income, top line vs bottom line, what is revenue, what are earnings, sales vs profit, income statement basics"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-eps', 'gross-vs-operating-margin', 'how-to-read-a-balance-sheet']}
    >
      <section>
        <h2>What is revenue?</h2>
        <p>
          Revenue is the total dollar value of everything a company sells in a given period — products, subscriptions, ads, services, anything customers pay for.
          It sits at the very top of the income statement, which is why it is often called the "top line".
        </p>
        <p>
          For Apple, revenue is iPhones plus Mac plus services plus accessories. For Netflix, it is monthly subscription fees. For Visa, it is the small percentage they take on every card swipe.
        </p>
      </section>

      <section>
        <h2>What are earnings?</h2>
        <p>
          Earnings (also called net income, profit, or "the bottom line") is what is left over after every single cost has been subtracted from revenue:
          cost of goods sold, operating expenses, interest, taxes, and one-off items.
        </p>
        <p>
          Earnings is the number that ends up in shareholders' pockets — either reinvested in the business, paid out as dividends, or used for buybacks.
          When you hear "EPS" or "P/E ratio", earnings is the engine those numbers run on. See our guide on <Link to="/blog/what-is-eps">EPS</Link>.
        </p>
      </section>

      <section>
        <h2>How do revenue and earnings relate to each other?</h2>
        <p>
          The income statement is just a chain of subtractions:
        </p>
        <ul>
          <li><strong>Revenue</strong></li>
          <li>− Cost of Goods Sold = <strong>Gross Profit</strong></li>
          <li>− Operating Expenses (R&D, marketing, G&A) = <strong>Operating Income</strong></li>
          <li>− Interest and Taxes = <strong>Net Income</strong> (earnings)</li>
        </ul>
        <p>
          Net margin (earnings ÷ revenue) tells you how much of every dollar of sales actually becomes profit. 5% is solid for retail; 25%+ is software-quality.
        </p>
      </section>

      <section>
        <h2>Why can earnings be misleading?</h2>
        <p>
          Revenue is harder to fake than earnings. Earnings can be massaged through:
        </p>
        <ul>
          <li><strong>One-off items</strong> like asset sales, lawsuit gains, or restructuring charges.</li>
          <li><strong>Depreciation choices</strong> that change how quickly assets are expensed.</li>
          <li><strong>Stock-based compensation</strong> that some companies exclude in "adjusted" earnings.</li>
        </ul>
        <p>
          Many investors prefer <Link to="/blog/what-is-free-cash-flow">free cash flow</Link> as a more honest measure than reported earnings.
        </p>
      </section>

      <section>
        <h2>Which should you focus on as an investor?</h2>
        <p>
          Use them together:
        </p>
        <ul>
          <li><strong>Revenue growth</strong> tells you whether the business is winning customers and gaining demand.</li>
          <li><strong>Earnings growth</strong> tells you whether that demand is translating into profit.</li>
          <li><strong>Margin expansion</strong> (earnings growing faster than revenue) is the holy grail — and the most common driver of multi-bagger returns.</li>
        </ul>
        <p>
          For more on how to combine these signals, see our <Link to="/blog/stock-picking">stock picking guide</Link>.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
